// External Libraries
import { useMutation } from "@apollo/client";
import Checkbox from "@material-ui/core/Checkbox";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatDistanceToNow, parseISO } from "date-fns";
import React, { Fragment, useEffect, useMemo, useState } from "react";

// Local Assets
import rightArrow from "../../../../../assets/icons/right-arrow.svg";

// External Utilities
import { uploadClient } from "../../../../..";
import { convertToString } from "../../../../../helper";
import { notify } from "../../../../Notify/Notify";

// GraphQL Queries
import {
  GET_REQUEST_DETAILS_BY_ID,
  SEND_DOCUMENT_REQUEST_SUBMISSION_REMINDER,
} from "../../../../RequestDetailedView/RequestDetailedView";

// Custom Components
import DocumentStatus from "../../../Popups/DocumentStatus/DocumentStatus";
import StatusChip from "../StatusChip/StatusChip";

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor("selection", {
    cell: ({ row }) => (
      <div className="">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    meta: {
      hasIndividualClick: true,
    },
    enableSorting: false,
  }),
  columnHelper.accessor("name", {
    cell: (props) => (
      <span style={{ color: "#020617" }}>{props.getValue()}</span>
    ),
    header: "Recipient",
  }),
  columnHelper.accessor("email", {
    cell: (props) => (
      <span style={{ color: "#020617" }}>{props.getValue()}</span>
    ),
    header: "Email Address",
  }),
  columnHelper.accessor("submittedDocumentsCount", {
    cell: (props) => (
      <span style={{ color: "#020617" }}>{`${props.getValue()} of ${
        props.row.original.documentsCount
      } Documents Submitted`}</span>
    ),
    header: "Submissions",
    enableSorting: false,
  }),
  columnHelper.accessor("status", {
    cell: (props) => (
      <Status
        value={props.getValue()?.toLowerCase()}
        userId={props.row?.original?.id}
        isOfPendingRequest={props.row?.original?.isOfPendingRequest}
        requestId={props.row?.original?.requestId}
        isWithinLast24Hours={checkIsWithinLast24Hours(
          props.row?.original?.lastReminderSent
        )}
      />
    ),
    header: "Status",
    meta: {
      hasIndividualClick: true,
    },
    enableSorting: false,
  }),

  columnHelper.accessor("lastReminderSent", {
    cell: (props) => (
      <span style={{ color: "#020617" }}>
        {convertToString(props.getValue()) === ""
          ? ""
          : getTimeSinceLastReminder(props.getValue())}
      </span>
    ),
    header: "Reminder sent on",
    meta: {
      hasIndividualClick: true,
    },
    enableSorting: false,
  }),
  columnHelper.accessor("fileMoved", {
    cell: (props) => (
      <span style={{ color: "#020617" }}>{props.getValue()}</span>
    ),
    header: "File Moved",
    enableSorting: true,
  }),
];

export default function useRequestDetailedViewTable(data = []) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: data?.length,
  });
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: (newRowSelection) => {
      setRowSelection(newRowSelection);
    },
    debugTable: false,
  });
  const getAllSelectedRowIds = () => {
    return table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row?.original?.id);
  };
  const getAllSelectedRows = () => {
    return table.getFilteredSelectedRowModel().rows.map((u) => u.original);
  };
  const resetAllRowSelection = () => {
    table.resetRowSelection();
  };
  return {
    table,
    globalFilter,
    setGlobalFilter,
    getAllSelectedRowIds,
    pageIndex,
    resetAllRowSelection,
    getAllSelectedRows,
  };
}

// helper components

function IndeterminateCheckbox({ indeterminate, className = "", ...rest }) {
  const ref = React.useRef(null);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <Checkbox
      color="primary"
      indeterminate={indeterminate}
      ref={ref}
      className={"cursor-pointer" + className}
      {...rest}
    />
  );
}

function Status({
  value,
  userId,
  isOfPendingRequest = false,
  requestId = "",
  isWithinLast24Hours = false,
}) {
  const [documentStatusPopupData, setDocumentStatusPopupData] = useState({
    isOpen: false,
    userId: "",
  });

  const [sendReminderToUsers, { loading: isSendReminderToUsersLoading }] =
    useMutation(SEND_DOCUMENT_REQUEST_SUBMISSION_REMINDER, {
      client: uploadClient,
      refetchQueries: [
        { query: GET_REQUEST_DETAILS_BY_ID, variables: { id: requestId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Send Reminder to Users Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Reminder(s) are successfully sent", "success");
      },
      awaitRefetchQueries: true,
    });

  const sendReminderHandler = async () => {
    if (isWithinLast24Hours || isSendReminderToUsersLoading) {
      return;
    }
    await sendReminderToUsers({
      variables: {
        documentRequestUserIds: [userId],
      },
    });
  };

  return (
    <Fragment>
      <div className="status-description-container w-100">
        <StatusChip
          value={value}
          onClickHandler={() =>
            setDocumentStatusPopupData({
              isOpen: true,
              userId: userId,
            })
          }
        />
        {isOfPendingRequest && value === "pending" && (
          <div className="d-flex">
            <div
              role="button"
              style={{
                color: "#2563EB",
                fontWeight: 510,
                paddingRight: "16px",
                opacity:
                  isWithinLast24Hours || isSendReminderToUsersLoading ? 0.5 : 1,
                cursor:
                  isWithinLast24Hours || isSendReminderToUsersLoading
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={() => sendReminderHandler()}
            >
              Send Reminder
            </div>
            <img src={rightArrow} alt="right_arrow" />
          </div>
        )}
      </div>
      <DocumentStatus
        isOpen={documentStatusPopupData.isOpen}
        onClose={() =>
          setDocumentStatusPopupData({
            isOpen: false,
            userId: "",
          })
        }
        userId={documentStatusPopupData.userId}
        sendReminderHandler={async () => {
          await sendReminderHandler();
          setDocumentStatusPopupData({
            isOpen: false,
            userId: "",
          });
        }}
        isSendReminderDisabled={
          isWithinLast24Hours || isSendReminderToUsersLoading
        }
      />
    </Fragment>
  );
}

// Helper functions
/**
 * Calculates the time elapsed since the last reminder sent.
 * @param {string} lastReminderSent - The date and time of the last reminder sent in ISO 8601 format.
 * @returns {string|null} A human-readable string representing the time elapsed since the last reminder sent,
 * or null if an error occurs during calculation.
 */
function getTimeSinceLastReminder(lastReminderSent) {
  try {
    const reminderTime = parseISO(lastReminderSent);
    const timeDifference = new Date() - reminderTime;

    if (isNaN(timeDifference)) {
      throw new Error("Invalid date format");
    }

    if (timeDifference < 0) {
      throw new Error("Future date provided");
    }

    if (timeDifference < 60 * 1000) {
      return "less than a minute ago";
    }

    return formatDistanceToNow(reminderTime, { addSuffix: true });
  } catch (error) {
    console.error(
      "Error occurred while calculating time difference:",
      error.message
    );
    return null;
  }
}

/**
 * Checks if the given time is within the last 24 hours from the current time.
 * @param {string} lastReminderSent - The date and time of the last reminder sent in ISO 8601 format.
 * @returns {boolean|null} True if the time is within the last 24 hours, false if not, or null if an error occurs.
 */
function checkIsWithinLast24Hours(lastReminderSent) {
  try {
    const reminderTime = new Date(lastReminderSent);
    const currentTime = new Date();

    if (isNaN(reminderTime.getTime())) {
      throw new Error("Invalid date format");
    }

    const timeDifference = currentTime.getTime() - reminderTime.getTime();
    const hours = Math.floor(timeDifference / (1000 * 60 * 60));

    return hours < 24;
  } catch (error) {
    console.error(
      "Error occurred while checking if within last 24 hours:",
      error.message
    );
    return null;
  }
}
